<template>
  <div class="nb" role="note" data-test="settings-notice">
    <WfIcon name="alert" :size="18" class="nb__icon" />
    <div class="nb__body">
      <div class="nb__title" data-test="settings-notice-title">{{ title }}</div>
      <div class="nb__text" data-test="settings-notice-text"><slot /></div>
      <a
        v-if="linkLabel && linkHref"
        class="nb__link"
        :href="linkHref"
        target="_blank"
        rel="noopener"
        data-test="settings-notice-link"
      >{{ linkLabel }}<WfIcon name="arrowR" :size="14" :stroke="2.2" /></a>
    </div>
  </div>
</template>

<script>
/**
 * The amber "this action can't work yet" box at the top of a settings panel (design frames 1n / 1p): a
 * title and a sentence or two, and (frame 1n) an optional link row under the text. Presentational.
 *
 * The link opens in a NEW TAB (`target="_blank"` with `rel="noopener"`): the office is in the middle of a
 * workflow and must not lose it. It is drawn only when BOTH `linkLabel` and `linkHref` are set, so a
 * caller that cannot work out where to go simply passes an empty href and no link shows.
 *
 * ⛔ No prop named patNum / siteID / loading / allSettings / isSuperAdmin / userID.
 */
import WfIcon from '../WfIcon.vue';

export default {
  name: 'NoticeBox',
  components: { WfIcon },
  props: {
    title: { type: String, required: true },
    /** The link row's text ('Open Settings'). '' = no link. */
    linkLabel: { type: String, default: '' },
    /** Where the link goes. '' = no link. */
    linkHref: { type: String, default: '' },
  },
};
</script>

<style lang="scss" scoped>
@import '../workflow-tokens';

.nb {
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid $wf-amber-line;
  border-radius: 12px;
  background: $fu-warning-subtle;
}

.nb__icon {
  margin-top: 1px;
  color: $fu-warning-text;
}

.nb__body { min-width: 0; }

.nb__title {
  font-size: 14px;
  font-weight: 700;
  line-height: 1.35;
  color: $fu-text;
}

.nb__text {
  margin-top: 3px;
  font-size: 13px;
  line-height: 1.5;
  color: $fu-neutral-700;
  overflow-wrap: anywhere;
}

// The link row. ⛔ `font-family: inherit`: the app's global stylesheet sets Montserrat on every `a`.
.nb__link {
  @include fu-link-btn;
  margin-top: 8px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;

  &:hover { text-decoration: underline; color: $fu-link; }

  // A finger needs more than a 13px line: grow the hit area without moving the text.
  @include wf-touch {
    padding: 11px 0;
    margin-top: -3px;
  }
}
</style>
